import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Field, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import classNames from 'classnames';
import type { History } from 'history';

import { ProductAmountInput, ProductAmountInputFormData } from './ProductAmountInput';
import { CartProductTabPanelProps } from './types';
import { sortOptionValuesAscending } from './utils';

import { Btn } from 'components/btn/Btn';
import {
    emptyGiftDetailsRequest,
    GiftDetailsRequest,
    ProductGiftDetails,
    ProductGiftDetailsContext,
} from './ProductGiftDetails';
import { scrollToElement } from 'common/utils/utils';
import { InMyWallet } from './InMyWallet';
import { useHistory, Switch, Route } from 'react-router-dom';
import { Routes } from 'routes';
import { isCardReloadable } from 'common/api/e-comm/models/Card';
import { useCardsFromWallet } from 'hooks/useCardsFromWallet';
import { Faceplate, getProductFaceplatesBff } from 'common/api/misc';
import { waitPromise } from 'utils/promiseHelpers';
import { FormBtn } from 'components/form-btn';
import { CartLineItemGiftDetails, emptyLineItem } from 'common/api/e-comm/models/CartLineItem';
import {
    useFirebaseConfigValues,
    firebaseValueAsNumber,
    firebaseValueAsBoolean,
    firebaseValueAsString,
} from 'hooks/useFirebaseConfigValues';
import { ProductVariant } from 'common/api/e-comm/models/Product';
import useUnavailableProductVariants from './utils/useUnavailableProductVariants';
import { Alert, AlertType } from 'components/alerts/sticky/Alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGiftCard, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { useModalControls } from 'hooks/useModalControls';
import type { ModalControls } from 'hooks/useModalControls';
import { Modal } from 'components/modal/Modal';
import BulkOrderScreen from 'features/bulk-order/BulkOrder';
import { useRootSelector } from 'common/features/featuresReducer';
import { selectCartNumberGiftLineItems } from 'common/features/store/duck/cart/duck';
import { colors } from 'styles/settings';
import { gaLogClickBuyNow, gaLogPurchase } from 'config/analytics';
import { TryBuyNowModal } from './TryBuyNow/TryBuyNow';
import { useBuyNow, BuyNowModal, useBuyNowParseQueryParams } from './buynow';
import { useBlackBoxValues } from 'modules/fraud-force/fraudForce';
import { OrderSource } from 'common/api/e-comm/models/OrderSource';
import { createTemporaryCartForBuyNow } from 'common/api/e-comm/cart';
import { buyNowPlaceOrder } from 'common/api/e-comm/orders/service';
import { useDispatch } from 'react-redux';
import { buyNowFlow } from 'common/features/store/duck/ui/buy-now-flow';
import { constructorClient } from 'third-party-integrations/constructor';
import { Codes, handleError } from 'common/features/store/duck/ui/buy-now-flow/errors';
import useFeatureFlags, { FeatureFlagTypes } from 'common/hooks/useFeatureFlags';
import { SendAsGiftUnavailable } from './SendAsGiftUnavailable';
import { ToShopInfo } from './ToShopInfo';

export interface ProductDetailsPanelProps extends CartProductTabPanelProps {
    value: string;
    brandId: number;
    brandName: string;
    brandImageUrl: string;
    resetKey?: number;
}

export const getGiftCardImgSrc = (imgs: Faceplate[], imageName: string) => {
    const img = imgs.find((img) => img.ImageName === imageName);

    if (img != null) {
        return img.ImageUrl;
    }

    return '';
};

const onlyNumbers = (phone: string) => {
    return (phone || '').replace(/[^0-9]/gi, '');
};

export const EGiftTabPanel = ({
    resetKey,
    value,
    product,
    brandName,
    brandId,
    brandImageUrl,
    addToCart,
    canCheckout,
}: ProductDetailsPanelProps) => {
    const buyNowControls = useBuyNow();
    const {
        selectedVariant: defaultVariant,
        priceOverride,
        shouldOpenBuyNow,
    } = useBuyNowParseQueryParams(product);
    const [showTryBuyNow, setShowTryBuyNow] = useState(false);
    const modalCtrls = useModalControls();
    const { retrieveBlackBox } = useBlackBoxValues();
    const dispatch = useDispatch<any>();
    const { isEnabled } = useFeatureFlags();

    const initialValues: ProductAmountInputFormData = {
        selectedOptions: Object.fromEntries(
            product.options.map((opt) => [
                opt.id,
                defaultVariant || sortOptionValuesAscending(opt.values)[0].value,
            ])
        ),
        priceOverride: priceOverride || product.minValue || null,
        quantity: 1,
        hideAddedToCartAnimation: false,
    };

    const [faceplates, setFaceplates] = useState<Faceplate[]>([]);
    const [selectedVariant, setSelectedVariant] = useState<ProductVariant | undefined>(undefined);

    const [sendAsGift, setSendAsGift] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [formData, setFormData] = useState<GiftDetailsRequest>(emptyGiftDetailsRequest);
    const [hasFormData, setHasFormData] = useState<boolean>(false);
    const showEditForm = sendAsGift && (isEditing || !hasFormData);
    const showGiftDetails = sendAsGift && !isEditing && hasFormData;
    const [giftCardImageUrl, setGiftCardImageUrl] = useState<string>('');
    const allowGiftingEgift = useFirebaseConfigValues<boolean>(
        'allow_gifting_egift',
        false,
        firebaseValueAsBoolean
    );
    const Max_Number_Gift_Line_Items = useFirebaseConfigValues(
        'max_cart_line_items',
        125,
        firebaseValueAsNumber
    );
    const numberOfGiftLineItems = useRootSelector(selectCartNumberGiftLineItems);

    const modal = useModalControls();
    const maxCapcityWarningModal = useModalControls();

    const history = useHistory();
    const { text, variants: unavailableVariants } = useUnavailableProductVariants(product);
    const allVariantsUnavailable = unavailableVariants.length === product.variants.length;

    const isFetchingBuyNowPaymentMethod = buyNowControls.fetching;
    const buyNowPaymentMethod = buyNowControls.paymentMethod;

    useEffect(() => {
        if (shouldOpenBuyNow && buyNowPaymentMethod && !isFetchingBuyNowPaymentMethod) {
            modalCtrls.toggleModalVisibility();
        } else if (shouldOpenBuyNow && !buyNowPaymentMethod && !isFetchingBuyNowPaymentMethod) {
            // CASE: User linked from browser-extension but doesn't have
            // a valid payment-method
            setShowTryBuyNow(true);
        }
    }, [shouldOpenBuyNow, isFetchingBuyNowPaymentMethod, buyNowPaymentMethod]);

    const userCards = useCardsFromWallet().filter(
        (c) => c.brandId === product.brandId && !isCardReloadable(c)
    );

    const thirdPartyViewerFlowIds = useFirebaseConfigValues<string>(
        'thirdparty_viewer_flow_ids',
        '',
        firebaseValueAsString
    );

    const shouldHideSendAsGift = useMemo(
        () =>
            isEnabled(FeatureFlagTypes.enable_amex_egift_web) &&
            thirdPartyViewerFlowIds?.split(',').includes(`${product.id}`),
        [product, isEnabled, thirdPartyViewerFlowIds]
    );

    const giftDetail = useMemo(() => {
        if (!hasFormData) {
            return;
        }

        return {
            fromName: formData.SenderName,
            fromPhone: onlyNumbers(formData.SenderPhone),
            toName: formData.RecipientName,
            toEmail: formData.RecipientEmail,
            message: formData.GiftMessage,
            deliveryDate: formData.SelectedDate?.toISOString(),
            productImageName: formData.SelectedCardCoverImage.toString(),
        } as CartLineItemGiftDetails;
    }, [hasFormData, formData]);

    const [addingToCart, setAddingToCart] = useState(false);

    const onSubmit = async (
        data: ProductAmountInputFormData,
        formikHelpers: FormikHelpers<ProductAmountInputFormData>
    ) => {
        // CASE: if you're not part of the organization anymore -- you shouldn't
        // be able to add items to cart
        if (!canCheckout) {
            return;
        }

        const apiResponse = await waitPromise(
            addToCart(product, data, giftDetail),
            setAddingToCart
        );

        // clear gift data after adding to cart + reset the form to the initial state
        if (apiResponse?.response?.ok) {
            resetFormData();
            setSendAsGift(false);

            formikHelpers.resetForm();
        }
    };

    useEffect(() => {
        discardGiftDetails();
    }, [resetKey]);

    useEffect(() => {
        const giftCardUrl = getGiftCardImgSrc(faceplates, formData?.SelectedCardCoverImage || '');
        setGiftCardImageUrl(giftCardUrl);
    }, [faceplates, formData?.SelectedCardCoverImage]);

    const sku = product.sku || selectedVariant?.sku;

    useEffect(() => {
        (async () => {
            if (sku) {
                const faceplates = await getProductFaceplatesBff(sku);

                if (faceplates?.response?.ok) {
                    const facePlates_ = faceplates?.data || [];
                    setFaceplates(facePlates_);

                    // NOTE: Special case -- we have loaded faceplates
                    // meaning that our form-data __should__ be updated
                    // with the new faceplate selection.
                    if (facePlates_.length) {
                        setFormData((formData) => ({
                            ...formData,
                            SelectedCardCoverImage: facePlates_[0].ImageName,
                        }));
                    }
                } else {
                    setFaceplates([]);
                }
            }
        })();
    }, [sku]);

    useEffect(() => {
        if (!formData.SelectedCardCoverImage && faceplates.length > 0) {
            setFormData({ ...formData, SelectedCardCoverImage: faceplates[0].ImageName });
        }
    }, [faceplates, formData]);

    const goToCardPage = (cardId: string) =>
        history.push(Routes.WalletMyCardDetails.replace(':cardId', cardId));

    const resetFormData = useCallback(() => {
        setFormData(emptyGiftDetailsRequest);
        setHasFormData(false);
    }, [setFormData, setHasFormData]);

    const setSelectedDate = useCallback(
        (selectedDate: Date | undefined) => {
            setFormData({
                ...formData,
                SelectedDate: selectedDate,
            });
        },
        [formData, setFormData]
    );

    const toggleSendAsGift = () => {
        if (numberOfGiftLineItems === Max_Number_Gift_Line_Items) {
            maxCapcityWarningModal.toggleModalVisibility();
        } else {
            setSendAsGift((_) => !_);
        }
    };

    const discardGiftDetails = () => {
        setFormData(emptyGiftDetailsRequest);
        setHasFormData(false);
        setSendAsGift(false);
        scrollToElement(document.body);
    };

    const discardGiftDetailsAndCloseModal = () => {
        discardGiftDetails();
        modal.toggleModalVisibility();
    };

    const onBuyNowClick = (formData: ProductAmountInputFormData) => {
        const selectedOption = Object.values(formData.selectedOptions)[0];
        let price = formData.priceOverride || 0;

        if (selectedOption) {
            const matches = selectedOption.match(/\d+\.\d+/g);
            if (matches && matches.length) {
                price = parseFloat(matches[0]);
            }
        }

        gaLogClickBuyNow(product.id, price, formData.quantity);

        if (buyNowControls.paymentMethod) {
            modalCtrls.toggleModalVisibility();
        } else {
            setShowTryBuyNow(true);
        }
    };

    const onConfirmPurchase = async (
        formikProps: FormikProps<ProductAmountInputFormData>,
        subtotal: number,
        fee: number,
        total: number
    ) => {
        if (formikProps.isValid) {
            const bbValues = await retrieveBlackBox();
            const tempCart = await createTemporaryCartForBuyNow();
            if (tempCart.data && bbValues.blackBoxValue && buyNowControls.paymentMethod) {
                dispatch(buyNowFlow.update({ buyNowDrawerView: 'Loading' }));
                const purchaseRequest = {
                    item: { ...formikProps.values, productId: product.id },
                    blackBoxValue: bbValues.blackBoxValue,
                    orderSource: OrderSource.Web,
                    clientVersion: '2.0.0',
                    paymentMethod: buyNowControls.paymentMethod,
                    paymentProviderResponse: null,
                    payment3DSecureResponse: null,
                    cartId: tempCart.data.id,
                };

                const result = await buyNowPlaceOrder(purchaseRequest);
                if (result.data) {
                    if (result.data.errorCode) {
                        const errorData = handleError(result.data);
                        dispatch(
                            buyNowFlow.update({
                                errorData,
                                buyNowDrawerView: 'Error',
                            })
                        );
                    } else {
                        const createdTime = new Date().toISOString();
                        const logData = {
                            cart: {
                                id: 'buy-now',
                                earnings: result.data.earnings || 0,
                                lineItems: [
                                    {
                                        ...emptyLineItem,
                                        id: 'buy-now',
                                        productId: product.id,
                                        price: formikProps.values.priceOverride || product.price,
                                        quantity: formikProps.values.quantity,
                                        earnings: result.data.earnings || 0,
                                    },
                                ],
                                productCount: 1,
                                subtotal: subtotal,
                                createdTime: createdTime,
                                updatedTime: createdTime,
                            },
                            total,
                            shippingCost: fee,
                            orderId: result.data.orderId ? String(result.data.orderId) : '',
                            isBuyNow: true,
                        };
                        gaLogPurchase(logData);
                        constructorClient.tracker.trackPurchase({
                            items: [{ itemId: brandId.toString(), itemName: brandName }] as any[],
                            revenue: total!,
                            orderId: result.data.orderId ? String(result.data.orderId) : '',
                            section: 'Products',
                        });
                        dispatch(
                            buyNowFlow.update({
                                buyNowDrawerView: 'Success',
                                purchaseSuccessData: {
                                    subtotal: subtotal,
                                    earnings: result.data.earnings || 0,
                                },
                            })
                        );
                    }
                } else {
                    // NOTE: Error Handling Code Goes Here
                    const errorData = handleError({
                        earnings: null,
                        errorCode: Codes.ECOMM_CATCH_ALL,
                        errorMessage: null,
                        orderId: null,
                    });
                    dispatch(
                        buyNowFlow.update({
                            errorData,
                            buyNowDrawerView: 'Error',
                        })
                    );
                }
            } else {
                // NOTE: We should render the `service unavailable message here`
                const errorData = handleError({
                    earnings: null,
                    errorCode: Codes.ECOMM_CATCH_ALL,
                    errorMessage: null,
                    orderId: null,
                });
                dispatch(
                    buyNowFlow.update({
                        errorData,
                        buyNowDrawerView: 'Error',
                    })
                );
            }
        }
    };

    return (
        <Tabs.Panel value={value}>
            <div
                className="mb-10 sm:mb-24"
                data-cnstrc-product-detail
                data-cnstrc-item-id={product.brandId}
                data-cnstrc-item-variation-id={selectedVariant?.id}
                data-cnstrc-item-name={brandName}
                data-cnstrc-item-price={selectedVariant?.price}
            >
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    key={product.id}
                >
                    {(formikProps) => (
                        <Switch>
                            <Route exact path={Routes.BrandsGiftCardDetails}>
                                <Form>
                                    {text ? (
                                        <Alert
                                            type={AlertType.INFO}
                                            className="my-5 sm:mt-0"
                                            title="Temporarily Unavailable"
                                        >
                                            <p>{text}</p>
                                        </Alert>
                                    ) : null}
                                    <div className="text-grey-darkest">
                                        Digital gift cards that are delivered electronically and
                                        automatically saved to your Wallet.
                                    </div>
                                    {shouldHideSendAsGift ? <ToShopInfo /> : null}
                                    <div className="mt-7">
                                        {!allVariantsUnavailable && (
                                            <ProductAmountInput
                                                product={product}
                                                label={
                                                    <span className="font-bold">eGift Value</span>
                                                }
                                                hideQuantityField={product?.canOverridePrice}
                                                setVariant={setSelectedVariant}
                                            />
                                        )}
                                    </div>
                                    <div className="block text-center md:text-right mt-12 mb-8">
                                        <div className="flex fwlex-col md:flex-row justify-end">
                                            {!showEditForm && (
                                                <>
                                                    {!allVariantsUnavailable && (
                                                        <div className="hidden md:inline-block text-center mb-2">
                                                            {!buyNowControls.isBuyNowEnabled ? (
                                                                <>
                                                                    <FormBtn
                                                                        type="submit"
                                                                        className="pdp_add_to_cart_button block w-72 text-base"
                                                                        isSubmitting={addingToCart}
                                                                        disabled={!canCheckout}
                                                                    >
                                                                        Add to Cart
                                                                    </FormBtn>
                                                                    {allowGiftingEgift &&
                                                                        !showGiftDetails &&
                                                                        (!shouldHideSendAsGift ? (
                                                                            <Btn
                                                                                type="button"
                                                                                isSecondary
                                                                                onClick={
                                                                                    toggleSendAsGift
                                                                                }
                                                                                className="block w-72 mt-3 text-base font-semibold"
                                                                            >
                                                                                <FontAwesomeIcon
                                                                                    className="mr-2"
                                                                                    icon={
                                                                                        faGiftCard
                                                                                    }
                                                                                />
                                                                                Send as Gift by
                                                                                Email
                                                                            </Btn>
                                                                        ) : (
                                                                            <SendAsGiftUnavailable />
                                                                        ))}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Btn
                                                                        type="button"
                                                                        className="block w-64 text-base mb-3"
                                                                        onClick={() =>
                                                                            onBuyNowClick(
                                                                                formikProps.values
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            buyNowControls.fetching ||
                                                                            showGiftDetails
                                                                        }
                                                                    >
                                                                        Buy Now
                                                                    </Btn>
                                                                    <FormBtn
                                                                        type="submit"
                                                                        isSecondary
                                                                        className="pdp_add_to_cart_button block w-64 text-base"
                                                                        isSubmitting={addingToCart}
                                                                        disabled={!canCheckout}
                                                                    >
                                                                        Add to Cart
                                                                    </FormBtn>
                                                                    {allowGiftingEgift &&
                                                                        !showGiftDetails &&
                                                                        (!shouldHideSendAsGift ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={
                                                                                    toggleSendAsGift
                                                                                }
                                                                                className="block w-64 mt-5 text-base text-brand font-semibold hover:underline"
                                                                            >
                                                                                <FontAwesomeIcon
                                                                                    className="mr-2"
                                                                                    icon={
                                                                                        faGiftCard
                                                                                    }
                                                                                />
                                                                                Send as Gift by
                                                                                Email
                                                                            </button>
                                                                        ) : (
                                                                            <SendAsGiftUnavailable />
                                                                        ))}
                                                                </>
                                                            )}
                                                            {showGiftDetails && (
                                                                <button
                                                                    onClick={
                                                                        modal.toggleModalVisibility
                                                                    }
                                                                    className="text-lg mx-auto mt-8 text-brand font-semibold block no-underline outline-none focus:outline-none"
                                                                    type="button"
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faTimes}
                                                                        size="lg"
                                                                    />
                                                                    <span className="ml-2">
                                                                        Cancel
                                                                    </span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {!showEditForm && (
                                                <>
                                                    {!allVariantsUnavailable && (
                                                        <div className="flex mt-8 md:hidden">
                                                            <div className="inline-block ml-auto md:mx-auto md:hidden text-center mb-2">
                                                                {!buyNowControls.isBuyNowEnabled ? (
                                                                    <>
                                                                        <FormBtn
                                                                            type="submit"
                                                                            className={classNames(
                                                                                'pdp_add_to_cart_button inline-block',
                                                                                showGiftDetails
                                                                                    ? 'w-48'
                                                                                    : 'w-80'
                                                                            )}
                                                                            isSubmitting={
                                                                                addingToCart
                                                                            }
                                                                            disabled={!canCheckout}
                                                                        >
                                                                            Add to Cart
                                                                        </FormBtn>
                                                                        {allowGiftingEgift &&
                                                                            !showGiftDetails &&
                                                                            (!shouldHideSendAsGift ? (
                                                                                <Btn
                                                                                    type="button"
                                                                                    isSecondary
                                                                                    onClick={
                                                                                        toggleSendAsGift
                                                                                    }
                                                                                    className="block w-80 mt-5 text-base"
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        className="mr-2"
                                                                                        icon={
                                                                                            faGiftCard
                                                                                        }
                                                                                    />
                                                                                    Send as Gift by
                                                                                    Email
                                                                                </Btn>
                                                                            ) : (
                                                                                <SendAsGiftUnavailable />
                                                                            ))}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Btn
                                                                            type="button"
                                                                            className="block w-80 text-base mb-3"
                                                                            onClick={() =>
                                                                                onBuyNowClick(
                                                                                    formikProps.values
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                buyNowControls.fetching ||
                                                                                showGiftDetails
                                                                            }
                                                                        >
                                                                            Buy Now
                                                                        </Btn>
                                                                        <FormBtn
                                                                            type="submit"
                                                                            className={classNames(
                                                                                'pdp_add_to_cart_button inline-block',
                                                                                showGiftDetails
                                                                                    ? 'w-48'
                                                                                    : 'w-80'
                                                                            )}
                                                                            isSecondary
                                                                            isSubmitting={
                                                                                addingToCart
                                                                            }
                                                                            disabled={!canCheckout}
                                                                        >
                                                                            Add to Cart
                                                                        </FormBtn>
                                                                        {allowGiftingEgift &&
                                                                            !showGiftDetails &&
                                                                            (!shouldHideSendAsGift ? (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={
                                                                                        toggleSendAsGift
                                                                                    }
                                                                                    className="block w-80 mt-5 text-base text-brand font-semibold"
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        className="mr-2"
                                                                                        icon={
                                                                                            faGiftCard
                                                                                        }
                                                                                    />
                                                                                    Send as Gift by
                                                                                    Email
                                                                                </button>
                                                                            ) : (
                                                                                <SendAsGiftUnavailable />
                                                                            ))}
                                                                    </>
                                                                )}
                                                                {showGiftDetails && (
                                                                    <Btn
                                                                        isSecondary
                                                                        onClick={
                                                                            modal.toggleModalVisibility
                                                                        }
                                                                        className="w-48 text-lg mx-auto mt-2 md:mt-8 text-brand font-semibold block no-underline outline-none focus:outline-none"
                                                                        type="button"
                                                                    >
                                                                        <span className="ml-2">
                                                                            Cancel
                                                                        </span>
                                                                    </Btn>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            <DiscardGiftDetailsWarningModal
                                                modal={modal}
                                                discardGiftDetails={discardGiftDetailsAndCloseModal}
                                            />
                                            <CartMaxCapacityGiftWarningModal
                                                modal={maxCapcityWarningModal}
                                                history={history}
                                            />
                                            <TryBuyNowModal
                                                isOpen={showTryBuyNow}
                                                onClose={() => setShowTryBuyNow(false)}
                                            />
                                            <Field type="hidden" name="hideAddedToCartAnimation" />
                                        </div>
                                    </div>
                                    <BuyNowModal
                                        itemDetails={formikProps.values}
                                        imageUrl={brandImageUrl}
                                        isOpen={modalCtrls.isVisible}
                                        onClose={modalCtrls.toggleModalVisibility}
                                        product={product}
                                        paymentMethod={buyNowControls.paymentMethod}
                                        onConfirmPurchase={onConfirmPurchase}
                                        brandName={brandName}
                                    />
                                </Form>
                                <ProductGiftDetailsContext.Provider
                                    value={{
                                        selectedDate: formData.SelectedDate,
                                        setSelectedDate,
                                        formData,
                                        setFormData,
                                        hasFormData,
                                        setHasFormData,
                                        isEditing,
                                        setIsEditing,
                                        product,
                                        sendAsGift,
                                        setSendAsGift,
                                        showEditForm,
                                        showGiftDetails,
                                        giftCardImages: faceplates,
                                        giftCardImageUrl,
                                    }}
                                >
                                    <ProductGiftDetails />
                                </ProductGiftDetailsContext.Provider>
                            </Route>
                            <Route path={Routes.BrandBulkOrder}>
                                <BulkOrderScreen
                                    facePlates={faceplates}
                                    brandName={brandName}
                                    brandImageUrl={brandImageUrl}
                                    product={product}
                                    closeGiftDetails={discardGiftDetails}
                                />
                            </Route>
                        </Switch>
                    )}
                </Formik>
            </div>
            <div>
                <InMyWallet
                    brandName={brandName}
                    items={userCards}
                    onClick={(card) => goToCardPage(card.id)}
                />
            </div>
        </Tabs.Panel>
    );
};

const CartMaxCapacityGiftWarningModal = ({
    modal,
    history,
}: {
    modal: ModalControls;
    history: History;
}) => {
    const goToCart = () => history.push(Routes.ShopOrderSummary);
    return (
        <Modal title="" isOpen={modal.isVisible} onClose={modal.toggleModalVisibility}>
            <div className="px-0 md:px-14 text-center">
                <FontAwesomeIcon
                    className="mx-auto"
                    size="2x"
                    color={colors.warningDark}
                    icon={faExclamationTriangle}
                />
                <h1 className="mx-auto mt-5 text-29px text-brand-dark font-semibold">
                    Your Cart is Full
                </h1>
                <p className="mt-4 text-grey-1 text-17px">
                    Please checkout with your current cart before placing additional orders.
                </p>
                <div className="flex flex-col gap-3 md:gap-5 mt-7 md:mt-14 w-52 mx-auto">
                    <Btn className="text-lg" onClick={goToCart}>
                        Go to Cart
                    </Btn>
                    <button
                        type="button"
                        className="underline text-brand font-semibold text-lg"
                        onClick={modal.toggleModalVisibility}
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};

const DiscardGiftDetailsWarningModal = ({
    modal,
    discardGiftDetails,
}: {
    modal: ModalControls;
    discardGiftDetails: () => void;
}) => {
    return (
        <Modal
            title="Are you sure?"
            isOpen={modal.isVisible}
            onClose={modal.toggleModalVisibility}
            titleStyles="text-29px"
        >
            <p className="text-grey-darkest text-base">
                By canceling your order, all gifting details will be removed and you will need to
                enter them again to send as an eGift.
            </p>
            <div className="flex flex-col gap-3 md:gap-5 mt-7 md:mt-14 w-52 mx-auto">
                <Btn className="text-lg" onClick={modal.toggleModalVisibility}>
                    Keep Gift Details
                </Btn>
                <button
                    type="button"
                    className="underline text-brand font-semibold text-lg"
                    onClick={discardGiftDetails}
                >
                    Delete Gift Details
                </button>
            </div>
        </Modal>
    );
};
